import React, { ReactElement } from "react";


const getApi = () : string => {
    if (window.location.hostname === "localhost")
        return window.location.protocol + '//' + window.location.hostname + ":35244/api";
    else
        return window.location.protocol + '//' + window.location.hostname + "/api";
}

// Query string for the optional developer token; empty for anonymous sessions
const tokenQuery = (token : string) : string => token ? `?token=${encodeURIComponent(token)}` : "";

type FetchProps = {
    name:string,
    submit:boolean,
    method:string,
    uri:string,
    onResult:Function,
    onError:Function,
    body?:string,
}

enum FetchState {
    begin,
    started,
    done,
    servererror,
    fetcherror,
}

const getFetchStateName = (s : FetchState) : string => {
    switch (s) {
        case FetchState.begin:
            return "begin";
        case FetchState.started:
            return "loading";
        case FetchState.done:
            return "done";
        case FetchState.servererror:
            return "server error";
        case FetchState.fetcherror:
            return "access error";
    }
    return "unknown";
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeout_ms : number) {    
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout_ms);
  
    const response = await fetch(input, {
      ...init,
      signal: controller.signal  
    });
    clearTimeout(id);  
    return response;
}

const Fetch = (props: FetchProps) : ReactElement => {
    const [state, setState] = React.useState<FetchState>(FetchState.begin);
    const uri = props.uri;
    React.useEffect(()=>{
        let cancelled = false;
        if (state !== FetchState.begin)
          return ()=>cancelled=true;
        if (!props.submit)
            return ()=>cancelled=true;
        setState(FetchState.started);
        const f = async () => {
          try {
            if (cancelled)
              return;
            console.log("Fetching: ", uri);
            let options:RequestInit|undefined = undefined;
            if (props.body && props.method === "POST" )
                options = {method:props.method, body:props.body}
            let fetched = await fetchWithTimeout(uri, options as RequestInit, 10000);
            let json = await fetched.json();
            console.log (json);
            if (json.error) {
                console.error(json);
                setState(()=>FetchState.servererror);
                props.onError(json.error);
            } else if (json.status) {
                console.error(json);
                setState(()=>FetchState.servererror);
                props.onError(json.status);
            } else {
                console.log(json);
                setState(()=>FetchState.done);
                props.onResult(json);
            }
          } catch (e) {
            console.log(e);
            if (e instanceof Error) {
                console.error(e.message);
                props.onError(e.message);
                setState(()=>FetchState.fetcherror);
            }
          }
        }
        f();
        return ()=>{cancelled = true};
      }, [props, state, uri]);
    return <div className="FetchStatus">{state === FetchState.begin ? <div/> : <div>Fetch: {props.name}: {getFetchStateName(state)}</div>}</div>
}


export type DipsApplication = {
    mapData? : string;
    name: string;
    crc: number;
    versions: string;
}

export type DipsApplications = {
    [crc : string] : DipsApplication | undefined;
}

type LoginProps = {
    submit : boolean,
    username : string,
    password : string,
    onTokenRightsApps : Function,
    onError : Function,
}
class LoginResult {
    token: string = "";
    rights: string = "";
    applications: DipsApplications = {};
}

export const LoginFetch = (props : LoginProps) : ReactElement => {
    return Fetch({
        name:"login",
        submit:props.submit,
        method:'GET', 
        uri:getApi()+`/login?username=${encodeURIComponent(props.username)}&password=${encodeURIComponent(props.password)}`, 
        onError:(e:string)=>{props.onError(e)}, 
        onResult:(r:LoginResult)=>{
            if (typeof (r.token) === 'string' && typeof(r.rights) === 'string' && typeof(r.applications) === 'object')
                props.onTokenRightsApps(r.token, r.rights, r.applications);
            else
                props.onError("Bad response from server");
            }});
}

type ApplicationsProps = {
    submit : boolean,
    onApplications : Function,
    onError : Function,
}

// Released applications, no login needed
export const ApplicationsFetch = (props : ApplicationsProps) : ReactElement => {
    return Fetch({
        name:"applications",
        submit:props.submit,
        method:'GET',
        uri:getApi()+`/applications`,
        onError:(e:string)=>{props.onError(e)},
        onResult:(r:DipsApplications)=>{
            if (r && typeof(r) === 'object' && !(r as any).error)
                props.onApplications(r);
            else
                props.onError("Bad response from server");
            }});
}

type MetadataProps = {
    token : string,
    setMetadata : Function,
    onError : Function,
}

export interface Field {
    id: number;
    name: string;
    type: string;
    min: number;
    max: number;
    size: number;
    translate: number;
    default: number;
    unit: string;
    doc: string;
    uiName: string;
    readOnly: boolean;
}

export class MetadataFieldMap {
    [key: string]: Field | undefined;
}

export type Metadata = {
    appCRC : number;
    name : string;
    settings : MetadataFieldMap;
    doc? : string;
}

export interface MetadataMap {
    [key: string]: Metadata | undefined;
}

export const MetadataFetch = (props : MetadataProps) : ReactElement => {
    return Fetch({
        name:"metadata",
        submit:true,
        method:"GET",
        uri:getApi()+`/allmetadata`+tokenQuery(props.token),
        onError:(e:string)=>{console.log("Error when fetching: " + e); props.onError("Failed to load metadata", e)},
        onResult:(r:Object) => {
            // Validate the returned data to be metadata
            let error = false;
            Object.keys(r).map((k)=>{
                let data : Metadata = (r as MetadataMap)[k] as Metadata;
                if (!data.name) {
                    props.onError("Missing name for application " + k);
                    error = true;
                }
                data.appCRC = parseInt(k);

                let fields:Array<string> = Object.keys(data.settings);
                for (let i : number = 0; i < fields.length; ++i) {
                    const field:string = fields[i];
                }
                return 0;
            });
            // Save it
            if (!error)
                props.setMetadata(r);
        }
    })
}

export type DownlinkData = {
    port : number,
    data : string,
}

export type Customization = {
    port : number,
    data : string,
    downlinks: Array<DownlinkData>,
    warning: string,
}

type CustomizationProps = {
    token : string,
    crc : number,
    onCustomization : Function,
    onError : Function,
    settings : Object,
}

export const CustomizationFetch = (props:CustomizationProps) => {
    return Fetch({
        name:"customization",
        submit:true,
        method:"POST",
        body:JSON.stringify({values:props.settings,crc:props.crc}),
        uri:getApi()+`/package-customization`+tokenQuery(props.token),
        onError:(e:string)=>{console.log("Error when fetching: " + e); props.onError("Failed to load customization", e)},
        onResult:(c:Object) => {
            if (!c.hasOwnProperty("port"))
                props.onError("Result has no port");
            else if (!c.hasOwnProperty("data"))
                props.onError("Result has no data");
            else
                props.onCustomization(c);
        }
    });
}

export type ProfileProps = {
    token : string;
    onProfilesMap : Function,
    onError : Function,
}

export type Profile = {
    port : number;
    data : string;
    name : string;
    icon : string;
    description : string;
    appCrc32 : number;
    configCrc32: number;
    custom? : boolean;
    synthesizedFromApp? : number;
    warning : string | undefined;
}

export type ApplicationProfiles = {
    [key: number]: Profile | undefined;
}

export type ProfilesMap = {
    [key: number]: ApplicationProfiles | undefined;
}

export const ProfilesFetch = (props : ProfileProps) => {
    return Fetch({
        name:"profiles",
        submit:true,
        method:"GET",
        uri:getApi()+`/allconfigs`+tokenQuery(props.token),
        onError:(e:string)=>{console.log("Error when fetching: " + e); props.onError("Failed to load profiles", e)},
        onResult:(p:Object) => {
            props.onProfilesMap(p as ProfilesMap);
        }
    })
}

type UnpackCustomizationFetchProps = {
    token:string;
    crc:number;
    port:number;
    data:string;
    onError:Function;
    onValues:Function;
}

export type ValueMap = {
    [key: string]: number | string | undefined;
}

type UnpackCustomizationResponse = {
    result : ValueMap;
}

export const UnpackCustomizationFetch = (props : UnpackCustomizationFetchProps) => {
    return Fetch({
        name:"unpack",
        submit:true,
        method:"POST",
        body:JSON.stringify({port:props.port,hexdata:props.data, crc:props.crc}),
        uri:getApi()+`/unpack`+tokenQuery(props.token),
        onError:(e:string)=>{console.log("Error when fetching: " + e); props.onError(e)},
        onResult:(c:UnpackCustomizationResponse) => {
            if (!c.hasOwnProperty("result"))
                props.onError("No result from unpack");
            else
                props.onValues(c.result);
        }
    });
   
}